import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pencil, Play, Plus, Square, Zap } from 'lucide-react';
import Button from 'components/crm/ui/Button';
import IconButton from 'components/crm/ui/IconButton';
import CrmScope from 'components/crm/ui/CrmScope';
import Spinner from 'components/crm/ui/Spinner';
import ToastProvider, { useToast } from 'components/crm/ui/Toast';
import PageForm from 'components/pageForm/PageForm';
import AddProspectDrawer from 'components/prospection/AddProspectDrawer';
import CallList from 'components/prospection/CallList';
import CallListBanner from 'components/prospection/CallListBanner';
import CallSheet from 'components/prospection/CallSheet';
import GoalBanner from 'components/prospection/GoalBanner';
import ProspectSelector from 'components/prospection/ProspectSelector';
import SalesRepOverview from 'components/prospection/SalesRepOverview';
import SessionHistory from 'components/prospection/SessionHistory';
import SessionSummary from 'components/prospection/SessionSummary';
import useCallWindowWarning from 'components/prospection/useCallWindowWarning';
import { SELECT_CLASS } from 'components/crm/ui/fieldStyles';
import { dial } from 'components/record/callAction';
import { apiErrorText, isCanceledRequest } from 'helper/apiErrorText';
import { COMMERCIAL_ROLE, SUPER_ADMIN_ROLE } from 'helper/roles';
import useCrmContext from 'hooks/useCrmContext';
import UseContext from 'hooks/useContext';
import { moveItem } from 'hooks/useDragReorder';
import useText from 'hooks/useText';
import {
  lineOfCompany,
  orderLines,
  upcomingCallbacks,
} from 'pages/prospection/callListItems';
import { decideLiveCall, nextCurrentCall } from 'pages/prospection/liveCall';
import crmApi from 'server/crm/crmApi';
import useCallDay from 'pages/prospection/useCallDay';
import {
  addToCallDay,
  connectSocket,
  endCallSession,
  getLiveCall,
  listCallSessions,
  listProspects,
  listTeam,
  reactivateProspect,
  removeFromCallDay,
  setCallOrder,
  startCallSession,
} from 'server/crm/prospectionApi';

const ENTRY_PARAMS = ['prospect', 'appeler', 'phoneCallId'];
const LIVE_CALL_EVENT = 'crmPhoneCall';

const findByCompany = (prospects, companyId) =>
  (prospects || []).find((item) => item.company?.id === companyId);

// The call of ?phoneCallId= is the one to qualify: it replaces another call of
// the same company on the sheet (phone/live at page opening answers the latest
// completed one, maybe not this one), unless that call is this one's later
// state or a call live now.
const keepsSheetCall = (sheetCall, phoneCallId) =>
  Boolean(sheetCall?.call) &&
  (sheetCall.call.id === phoneCallId || sheetCall.call.status !== 'completed');

// The screen itself, under the kit's ToastProvider so that it can toast.
// `paused`: another's list is shown in its place (agency manager); it stays
// mounted, hidden, so that coming back finds it as it was, but its sheet's
// shortcuts are off and live Wildix calls are ignored meanwhile.
const CallDayScreen = ({ paused = false }) => {
  const { text, language } = useText('prospection');
  const toast = useToast();
  const {
    day,
    now,
    loading,
    error,
    reloadDay,
    globalProspects,

    reloadGlobal,
  } = useCallDay();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openCall, setOpenCall] = useState(null);
  // The open sheet as last set, for the live call handler: an update can come
  // between a setOpenCall and the render that applies it.
  const openCallRef = useRef(null);
  openCallRef.current = openCall;
  const [highlightId, setHighlightId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // « Lancer »: a call session (its id), whose calls follow one another, each
  // confirmed call opening the next one, until the list is done or the sheet
  // closed. Another one may follow: the goal of the day is no limit.
  const [run, setRun] = useState(null);
  const advance = useRef(false);
  // The order of the queue as just dragged, shown at once; it holds for the day
  // it was dragged on, a day read again coming in the order the api keeps.
  const [dragged, setDragged] = useState({ day: null, ids: [] });
  const pending = useMemo(
    () =>
      orderLines(day?.pending ?? [], dragged.day === day ? dragged.ids : []),
    [day, dragged],
  );
  const { call: callCrm, me } = useCrmContext();
  // Every call this screen dials (« Lancer », the next call of a session) goes
  // out, with a warning outside the call window.
  const dialWithWarning = useCallWindowWarning(day?.callRules, dial);
  // The company of ?appeler= (and the Wildix call of ?phoneCallId=, a call to
  // qualify from Home), kept until the day and the global list are read.
  const pendingCallId = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Links from other screens: read once, then taken out of the address.
  useEffect(() => {
    if (!ENTRY_PARAMS.some((name) => searchParams.has(name))) return;
    const prospect = searchParams.get('prospect');
    const appeler = searchParams.get('appeler');
    if (prospect) setHighlightId(prospect);
    if (appeler) {
      const phoneCallId = searchParams.get('phoneCallId') || null;
      pendingCallId.current = { companyId: appeler, phoneCallId };
      if (phoneCallId) readEntryCall(appeler, phoneCallId);
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        ENTRY_PARAMS.forEach((name) => next.delete(name));
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // The Wildix call shown on the open sheet: { companyId, call }.
  const [sheetCall, setSheetCall] = useState(null);
  const sheetCallRef = useRef(null);
  sheetCallRef.current = sheetCall;
  // The company of ?appeler= while its sheet is being looked up among all
  // one's prospects: a live call takes it as the open sheet meanwhile.
  const openingEntry = useRef(null);
  // The Wildix call of ?phoneCallId=, read by its id: { companyId, call },
  // until the entry's sheet opens and takes it.
  const entryCall = useRef(null);

  // The sheet of a prospect, from the day (its line), the global list, or any
  // prospect of one's own; liveCall, when given, shows on it; phoneCallId,
  // when given, is the Wildix call its confirmation qualifies.
  const openSheetOf = (
    companyId,
    liveCall = null,
    phoneCallId = null,
    fromEntry = false,
  ) => {
    const show = (company, line) => {
      const nextOpen = { company, line, phoneCallId };
      // A live call that came while the entry's sheet was looked up stays on it.
      const keptCall =
        fromEntry && sheetCallRef.current?.companyId === company.id
          ? sheetCallRef.current
          : null;
      const keptEntryCall =
        fromEntry && entryCall.current?.companyId === company.id
          ? entryCall.current
          : null;
      if (fromEntry) entryCall.current = null;
      const nextSheetCall = liveCall
        ? { companyId: company.id, call: liveCall }
        : keptEntryCall && !keepsSheetCall(keptCall, phoneCallId)
          ? keptEntryCall
          : (keptCall ?? keptEntryCall);
      if (keptEntryCall && nextSheetCall === keptEntryCall) {
        followed.current = keptEntryCall.call;
      }
      openCallRef.current = nextOpen;
      sheetCallRef.current = nextSheetCall;
      setOpenCall(nextOpen);
      setSheetCall(nextSheetCall);
    };
    const line = lineOfCompany(day.pending, companyId);
    if (line) {
      setHighlightId(null);
      show(line.company, line);
      return;
    }
    const prospect = findByCompany(globalProspects, companyId);
    if (prospect) {
      show(prospect.company, null);
      return;
    }
    if (fromEntry) openingEntry.current = companyId;
    const settle = () => {
      if (fromEntry && openingEntry.current === companyId) {
        openingEntry.current = null;
      }
    };
    listProspects().then(
      (result) => {
        settle();
        const found = findByCompany(result?.prospects, companyId);
        if (found && mounted.current) show(found.company, null);
      },
      // Not one of one's prospects, or gone: nothing opens.
      settle,
    );
  };

  // A call to qualify from Home has no live call coming: phone/live reads it by
  // its id, and it shows on the entry's sheet as if the socket had brought it.
  // A null answer or a failure leaves the sheet without Wildix, silently.
  const readEntryCall = (companyId, phoneCallId) => {
    getLiveCall({ phoneCallId }).then(
      (liveCall) => {
        if (!mounted.current || liveCall?.id !== phoneCallId) return;
        const open = openCallRef.current;
        if (
          open?.company?.id === companyId &&
          open.phoneCallId === phoneCallId
        ) {
          const onSheet =
            sheetCallRef.current?.companyId === companyId
              ? sheetCallRef.current
              : null;
          if (keepsSheetCall(onSheet, phoneCallId)) return;
          const nextSheetCall = { companyId, call: liveCall };
          sheetCallRef.current = nextSheetCall;
          followed.current = liveCall;
          setSheetCall(nextSheetCall);
          return;
        }
        // The sheet is still to open: it takes the call then.
        const entryPending =
          pendingCallId.current?.companyId === companyId ||
          openingEntry.current === companyId;
        if (entryPending) entryCall.current = { companyId, call: liveCall };
      },
      () => {},
    );
  };

  // The call of ?appeler= is already dialed by the screen that sent here: only
  // its sheet opens.
  useEffect(() => {
    const pendingCall = pendingCallId.current;
    if (!pendingCall || !day || globalProspects === null) return;
    pendingCallId.current = null;
    openSheetOf(pendingCall.companyId, null, pendingCall.phoneCallId, true);
  }, [day, globalProspects]);

  const fail = useCallback(
    (failure) => {
      if (isCanceledRequest(failure)) return;
      toast(apiErrorText(failure, { language, text, group: 'errors' }), 'error');
    },
    [language, toast, text],
  );

  // Live Wildix calls (JBWEB-1400). `followed`: the last call the screen
  // followed. An update that comes before the day and the global list are
  // read waits for them (the latest one only: it carries the call's state).
  const followed = useRef(null);
  const waitingLiveCall = useRef(null);
  const handleLiveCall = (liveCall) => {
    if (paused) return;
    if (!day || globalProspects === null) {
      waitingLiveCall.current = liveCall;
      return;
    }
    // Read through refs: this can run in the same tick as the ?appeler= entry
    // that just opened a sheet, before any render applies it.
    const open = openCallRef.current;
    const current = sheetCallRef.current;
    const entryId = open
      ? null
      : (openingEntry.current ?? pendingCallId.current?.companyId ?? null);
    const openId = open?.company?.id ?? entryId;
    const sheet = open
      ? {
          company: open.company,
          callId: current?.companyId === openId ? current.call.id : null,
        }
      : entryId
        ? { company: { id: entryId }, callId: null }
        : null;
    const decision = decideLiveCall({
      call: liveCall,
      current: followed.current,
      sheet,
    });
    followed.current = nextCurrentCall(followed.current, liveCall, decision);
    if (decision.action === 'stay') {
      const nextSheetCall = { companyId: openId, call: liveCall };
      sheetCallRef.current = nextSheetCall;
      setSheetCall(nextSheetCall);
    } else if (decision.action === 'open') {
      openSheetOf(decision.companyId, liveCall);
    } else if (decision.action === 'toast') {
      toast(
        text('live.unknownNumber', {
          number: decision.number || text('live.hiddenNumber'),
        }),
        'error',
      );
    }
  };
  // The socket and phone/live call the latest handler, which reads this render's state.
  const liveCallHandler = useRef(handleLiveCall);
  liveCallHandler.current = handleLiveCall;

  useEffect(() => {
    if (!waitingLiveCall.current || !day || globalProspects === null) return;
    const liveCall = waitingLiveCall.current;
    waitingLiveCall.current = null;
    liveCallHandler.current(liveCall);
  }, [day, globalProspects]);

  // While the page is mounted: the call live at opening, then each update.
  useEffect(() => {
    let active = true;
    let socket = null;
    let updated = false;
    const onLiveCall = (liveCall) => {
      updated = true;
      liveCallHandler.current(liveCall);
    };
    connectSocket().then(
      (connected) => {
        if (!active || !connected) return;
        socket = connected;
        socket.on(LIVE_CALL_EVENT, onLiveCall);
      },
      () => {},
    );
    getLiveCall().then(
      (liveCall) => {
        // An update of the socket is newer than this answer.
        if (active && liveCall && !updated) liveCallHandler.current(liveCall);
      },
      () => {},
    );
    return () => {
      active = false;
      if (socket) socket.off(LIVE_CALL_EVENT, onLiveCall);
    };
  }, []);
  // An order the api refuses is told, and the day read again in the order it keeps.
  const moveQueue = (from, to) => {
    const ids = moveItem(
      pending.filter((line) => line.kind === 'new').map((line) => line.id),
      from,
      to,
    );
    setDragged({ day, ids });
    setCallOrder({ ids }).catch((failure) => {
      setDragged({ day: null, ids: [] });
      fail(failure);
      reloadDay();
    });
  };
  // One's prospects, for the callbacks planned on later days (« Relances planifiées »).
  const [prospects, setProspects] = useState([]);
  const reloadProspects = useCallback(
    () =>
      listProspects().then(
        (answer) => setProspects(answer?.prospects ?? []),
        () => setProspects([]),
      ),
    [],
  );
  useEffect(() => {
    reloadProspects();
  }, [reloadProspects]);
  const upcoming = upcomingCallbacks(prospects, now);
  const reloadAll = () => {
    reloadProspects();
    reloadDay();
    reloadGlobal();
  };

  // The highlight from another screen ends with the first call: it does not
  // come back when the sheet closes.
  const call = (line) => {
    setHighlightId(null);
    dialWithWarning(line.company?.phone);
    setOpenCall({ company: line.company, line });
    setSheetCall(null);
  };

  // The calls of the list, in its order: a session opens on them, then the first is called.
  const launch = () => {
    if (!pending.length || run) return;
    setSummary(null);
    startCallSession({ planned: pending.length }).then((session) => {
      setRun({ id: session.id });
      call(pending[0]);
    }, fail);
  };
  // The right column, while no call is open: one's past sessions, or the
  // summary of one (`summary`: { session, justEnded }).
  const [sessions, setSessions] = useState(null);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    listCallSessions().then(
      (list) => setSessions(Array.isArray(list) ? list : []),
      (failure) => {
        setSessions([]);
        fail(failure);
      },
    );
  }, [fail]);
  // The session closes with the calls left aside (none when the list is
  // done); what it gave shows in its place, and it joins the sessions.
  const endRun = (left) => {
    if (!run) return;
    endCallSession({ id: run.id, skipped: left }).then((ended) => {
      setSummary({ session: ended, justEnded: true });
      setSessions((list) => [
        ended,
        ...(list ?? []).filter((one) => one.id !== ended.id),
      ]);
    }, fail);
    setRun(null);
  };
  // « Terminer »: the call open, if any, is left as it is.
  const finish = () => {
    advance.current = false;
    endRun(pending.length);
    setOpenCall(null);
    setSheetCall(null);
  };
  const confirmed = () => {
    if (run) advance.current = true;
    reloadDay();
  };
  const closeSheet = () => {
    advance.current = false;
    endRun(pending.length);
    setOpenCall(null);
    setSheetCall(null);
  };
  // The day read again after a confirmed call: the next one, or the end.
  useEffect(() => {
    if (!advance.current || !day) return;
    advance.current = false;
    if (pending[0]) call(pending[0]);
    else {
      endRun(0);
      setOpenCall(null);
    }
  }, [day]);

  // Calls added to the day belong to its session (R14).
  const addPayload = (prospect) => ({
    companyId: prospect.company.id,
    sessionId: day?.daySession?.id,
  });

  // One prospect into the day: a free one is first taken (it becomes one's own).
  const addChoice = async (choice) => {
    if (choice.source === 'free') {
      await callCrm(crmApi.assignCompany, {
        companyId: choice.companyId,
        role: 'commercial',
        personId: me?.id,
      });
    }
    // Put aside (« Pas intéressé »): back to prospection, so that the day takes it.
    if (choice.reactivate)
      await reactivateProspect({ companyId: choice.companyId });
    await addToCallDay(addPayload(choice));
  };

  // Composing the list: on arrival with an empty list, or after « Modifier ».
  // `picked`: the companies ticked, starting from those of the list.
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState(new Map());
  const [saving, setSaving] = useState(false);
  const composing = Boolean(day) && !loading && (editing || !pending.length);
  const startComposing = () => {
    setPicked(
      new Map(
        pending
          .filter((line) => line.company?.id)
          .map((line) => [
            line.company.id,
            { companyId: line.company.id, source: 'line', line },
          ]),
      ),
    );
    setEditing(true);
  };
  const toggle = (choice) =>
    setPicked((current) => {
      const next = new Map(current);
      if (next.has(choice.companyId)) next.delete(choice.companyId);
      else next.set(choice.companyId, choice);
      return next;
    });
  const toAdd = [...picked.values()].filter(
    (choice) => choice.source !== 'line',
  );
  const toRemove = pending.filter((line) => !picked.has(line.company?.id));
  // The ticks applied one after another; a refusal is said and the others go on.
  const saveList = async () => {
    setSaving(true);
    for (const line of toRemove) {
      await removeFromCallDay({ callbackId: line.id }).catch(fail);
    }
    for (const choice of toAdd) {
      await addChoice(choice).catch(fail);
    }
    setSaving(false);
    setEditing(false);
    setPicked(new Map());
    reloadAll();
  };
  // A prospect created while composing and taken by oneself is already in the
  // day (its callback says so): ticked as a line of the list, so that saving
  // does not remove it, and the composition stays open with the ticks made so
  // far, which its arrival in the day would otherwise close and lose.
  const prospectAdded = (result) => {
    const companyId = result?.callback ? result?.company?.id : null;
    if (composing && companyId) {
      setPicked((current) =>
        new Map(current).set(companyId, { companyId, source: 'line' }),
      );
      setEditing(true);
    }
    reloadAll();
  };
  let saveLabel = `Ajouter (${toAdd.length})`;
  if (toRemove.length && !toAdd.length)
    saveLabel = `Retirer (${toRemove.length})`;
  else if (toRemove.length) saveLabel = 'Enregistrer';

  // On the line of « À appeler »: the pencil to change the list, then
  // « Lancer », « Terminer » while a session runs.
  const queueActions = (
    <div className="flex items-center gap-2">
      <IconButton
        bordered
        icon={Pencil}
        label="Modifier la liste"
        disabled={Boolean(run)}
        onClick={startComposing}
      />
      {run ? (
        <Button icon={Square} onClick={finish}>
          Terminer
        </Button>
      ) : (
        <Button icon={Play} disabled={!pending.length} onClick={launch}>
          Lancer
        </Button>
      )}
    </div>
  );

  const openCompanyId = openCall?.company?.id ?? null;
  const openSheetCall =
    sheetCall && sheetCall.companyId === openCompanyId ? sheetCall.call : null;
  // Off session, a confirmed sheet offers the next call of the list, opened
  // without dialing, or the end panel; a session goes on by itself.
  const nextLine = pending.find((line) => line.company?.id !== openCompanyId);
  let afterConfirm = null;
  if (!run) {
    afterConfirm = nextLine
      ? {
          kind: 'next',
          onClick: () => {
            setHighlightId(null);
            setOpenCall({ company: nextLine.company, line: nextLine });
          },
        }
      : { kind: 'summary', onClick: closeSheet };
  }
  // How far the day is: the calls made against the goals of the day.
  const goal = day
    ? (day.counts?.discovery?.goal ?? 0) + (day.counts?.followup?.goal ?? 0)
    : 0;
  const made = day?.made?.length ?? 0;

  return (
    <div
      data-testid="own-call-day"
      hidden={paused}
      className={paused ? 'hidden' : 'flex min-h-0 flex-1 flex-col'}
    >
      <div className="flex min-h-0 flex-1 gap-6 px-8 pb-6 pt-6">
        {/* One column: one's list, then the prospects to add to it. The call
            sheet only takes the right while a call is open. */}
        <section className="flex min-h-0 w-[560px] shrink-0 flex-col">
          {loading || !day ? (
            <CallList
              day={day}
              loading={loading}
              error={error}
              now={now}
              simple
            />
          ) : composing ? (
            <>
              {/* The list scrolls; its buttons stay at the bottom, whatever its length. */}
              <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                <GoalBanner
                  left={Math.max(0, goal - made)}
                  picked={editing ? picked.size : toAdd.length}
                />
                <ProspectSelector
                  lines={editing ? pending : []}
                  made={day.made ?? []}
                  selected={new Set(picked.keys())}
                  onToggle={toggle}
                />
              </div>
              <div className="flex shrink-0 justify-end gap-2 border-t border-line bg-white pt-3">
                {/* A prospect not followed yet: created here; taken by oneself, it comes in ticked. */}
                <Button
                  variant="secondary"
                  icon={Plus}
                  className="mr-auto"
                  onClick={() => setAddOpen(true)}
                >
                  Nouveau prospect
                </Button>
                {editing && pending.length > 0 && (
                  <Button variant="secondary" onClick={() => setEditing(false)}>
                    Annuler
                  </Button>
                )}
                <Button
                  disabled={saving || (!toAdd.length && !toRemove.length)}
                  onClick={saveList}
                >
                  {saveLabel}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
              {/* Where the day stands against its goals: the calls and the follow-ups. */}
              <div className="mb-4">
                <CallListBanner day={day} editing={false} />
              </div>
              <CallList
                day={{ ...day, pending }}
                loading={false}
                error={error}
                now={now}
                onMoveQueue={moveQueue}
                selectedCompanyId={openCompanyId ?? highlightId}
                openCompanyId={openCompanyId}
                queueActions={queueActions}
                upcoming={upcoming}
                simple
              />
            </div>
          )}
        </section>
        {/* While the day loads, the sessions draw their skeleton beside the list's. */}
        {!openCall && (day || loading) && (
          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            {summary ? (
              <SessionSummary
                session={summary.session}
                justEnded={summary.justEnded}
                onBack={() => setSummary(null)}
              />
            ) : (
              <SessionHistory
                sessions={sessions}
                onOpen={(session) => setSummary({ session, justEnded: false })}
              />
            )}
          </section>
        )}
        {openCall && (
          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            <CallSheet
              key={`${openCall.company?.id}:${openCall.line?.id ?? ''}`}
              company={openCall.company}
              line={openCall.line}
              // A call of a session counts in it; otherwise in the day's.
              sessionId={run?.id ?? day?.daySession?.id}
              onClose={closeSheet}
              onConfirmed={confirmed}
              afterConfirm={afterConfirm}
              liveCall={openSheetCall}
              phoneCallId={openCall.phoneCallId ?? null}
              shortcutsEnabled={!paused}
              callRules={day?.callRules}
            />
          </section>
        )}
      </div>
      <AddProspectDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={prospectAdded}
        todayList={{ sessionId: day?.daySession?.id }}
      />
    </div>
  );
};

const fullName = (person) =>
  [person.firstname, person.lastname].filter(Boolean).join(' ');

// The agency manager (500) picks whose list he sees: his own (« Moi »), or,
// read-only, that of an active Commercial (510). Anyone else sees his own only.
const ProspectionScreen = () => {
  const { text } = useText('prospection');
  const { userData } = UseContext();
  const managesTeam = Number(userData?.role) === SUPER_ADMIN_ROLE;
  const [salesReps, setSalesReps] = useState([]);
  const [viewedId, setViewedId] = useState('');

  // The team the api sends holds active users only.
  useEffect(() => {
    if (!managesTeam) return undefined;
    let active = true;
    listTeam().then(
      (team) => {
        if (!active) return;
        setSalesReps(
          (team ?? []).filter(
            (person) => Number(person.role) === COMMERCIAL_ROLE,
          ),
        );
      },
      () => {
        if (active) setSalesReps([]);
      },
    );
    return () => {
      active = false;
    };
  }, [managesTeam]);

  const viewed = managesTeam
    ? (salesReps.find((person) => person.id === viewedId) ?? null)
    : null;

  return (
    <>
      {managesTeam && (
        <div className="flex shrink-0 items-center gap-3 px-8 pt-6">
          <label
            htmlFor="prospection-list-of"
            className="text-sm font-medium text-ink"
          >
            {text('overview.listOf')}
          </label>
          <select
            id="prospection-list-of"
            className={SELECT_CLASS}
            value={viewed ? viewed.id : ''}
            onChange={(event) => setViewedId(event.target.value)}
          >
            <option value="">{text('overview.me')}</option>
            {salesReps.map((person) => (
              <option key={person.id} value={person.id}>
                {fullName(person)}
              </option>
            ))}
          </select>
        </div>
      )}
      <CallDayScreen paused={Boolean(viewed)} />
      {viewed && <SalesRepOverview key={viewed.id} owner={viewed} />}
    </>
  );
};

// Today's call list (JBWEB-1394): the list on the left, and on the right the
// call sheet, the waiting panel or the end panel. The app.admin shell (menu,
// Intercom) stays; everything inside it is drawn with the CRM kit.
const ProspectionPage = () => {
  const { text } = useText('prospection');

  const title = (
    <span className="flex items-center gap-2">
      <Zap size={20} className="fill-current" />
      {text('title')}
    </span>
  );

  return (
    <PageForm title={title}>
      <CrmScope className="flex min-h-0 flex-1 flex-col">
        <ToastProvider>
          <ProspectionScreen />
        </ToastProvider>
      </CrmScope>
    </PageForm>
  );
};

export default ProspectionPage;
